import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import { formatTimestamp } from '../../utils/timeUtils';
import LogoImage from '../../assets/images/croppedlogo.png';

const styles = StyleSheet.create({
    headerContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingLeft: 50,
        paddingRight: 50,
        backgroundColor: '#F4F5F7',
        borderRadius: 3,
    },
    headerInfoContainer: {
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    headerLogoContainer: {
        flexDirection: 'column',
    },
    headerLogo: {
        width: 80,
    },
    headerTitle: {
        fontSize: 25,
        fontWeight: 'bold',
        color: '#000000',
        marginBottom: 8,
    },
    headerInfo: {
        fontSize: 12,
        color: '#000000',
        marginBottom: 10,
    },
    page: {
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        padding: 20,
    },
    section: {
        flexGrow: 1,
    },
    header: {
        fontSize: 18,
        marginTop: 3,
        marginBottom: 10,
        fontWeight: 'bold',
        color: '#2C3E50'
    },
    info: {
        fontSize: 14,
        marginBottom: 5,
        color: '#34495E',
        marginLeft: 10
    },
    table: {
        display: 'table',
        width: 'auto',
        borderStyle: 'solid',
        borderColor: 'white',
    },
    tableRow: {
        flexDirection: 'row',
        borderStyle: 'solid',
        borderColor: 'white',
        borderWidth: 1,
        backgroundColor: '#F4F5F7',
    },
    tableColHeader: {
        width: '50%',
        backgroundColor: '#BCDDE0',
        padding: 5,
    },
    tableCol: {
        width: '50%',
        padding: 5,
    },
    tableCellHeader: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#000000',
    },
    tableCell: {
        fontSize: 10,
        color: '#000000',
    },
    imageContainer: {
        marginBottom: 5,
        marginTop: 5,
        borderRadius: 3,
    },
    chartImage: {
        width: '100%',
        height: 'auto'
    },
    imageTitle: {
        fontSize: 14,
        fontWeight: 'bold',
    },
});

const PDFDocument = ({ trialInfo, summaryData, tableContent, patientInfo, pdfCharts }) => (
    <Document>
        <Page size="A4" style={styles.page}>
            <View style={styles.section}>
                {/* Header */}
                <View style={styles.headerContainer}>
                    <View style={styles.headerInfoContainer}>
                        <Text style={styles.headerTitle}>Patient Trial Report</Text>
                        <Text style={styles.headerInfo}>
                            Clinic: Evolution PT{'\n'}
                            Generated Date: {formatTimestamp(new Date())}
                        </Text>
                    </View>
                    <View style={styles.headerLogoContainer}>
                        <Image style={styles.headerLogo} src={LogoImage} />
                    </View>
                </View>

                <Text style={styles.header}>Basic Information</Text>
                {/* Information table */}
                <View style={styles.table}>
                    {/* Table Header */}
                    <View style={styles.tableRow}>
                        <View style={styles.tableColHeader}>
                            <Text style={styles.tableCellHeader}>Information</Text>
                        </View>
                        <View style={styles.tableColHeader}>
                            <Text style={styles.tableCellHeader}>Content</Text>
                        </View>
                    </View>
                    {/* Table Rows */}
                    <View style={styles.tableRow}>
                        <View style={styles.tableCol}>
                            <Text style={styles.tableCellHeader}>Patient Name</Text>
                        </View>
                        <View style={styles.tableCol}>
                            <Text style={styles.tableCell}>{patientInfo.name}</Text>
                        </View>
                    </View>
                    <View style={styles.tableRow}>
                        <View style={styles.tableCol}>
                            <Text style={styles.tableCellHeader}>Patient Email</Text>
                        </View>
                        <View style={styles.tableCol}>
                            <Text style={styles.tableCell}>{patientInfo.email}</Text>
                        </View>
                    </View>
                    <View style={styles.tableRow}>
                        <View style={styles.tableCol}>
                            <Text style={styles.tableCellHeader}>Leg Side</Text>
                        </View>
                        <View style={styles.tableCol}>
                            <Text style={styles.tableCell}>{summaryData.deviceLocation}</Text>
                        </View>
                    </View>
                    <View style={styles.tableRow}>
                        <View style={styles.tableCol}>
                            <Text style={styles.tableCellHeader}>Trial Date</Text>
                        </View>
                        <View style={styles.tableCol}>
                            <Text style={styles.tableCell}>{trialInfo.date}</Text>
                        </View>
                    </View>
                    <View style={styles.tableRow}>
                        <View style={styles.tableCol}>
                            <Text style={styles.tableCellHeader}>Trial Period</Text>
                        </View>
                        <View style={styles.tableCol}>
                            <Text style={styles.tableCell}>{trialInfo.timePeriod}</Text>
                        </View>
                    </View>
                    <View style={styles.tableRow}>
                        <View style={styles.tableCol}>
                            <Text style={styles.tableCellHeader}>Session Type</Text>
                        </View>
                        <View style={styles.tableCol}>
                            <Text style={styles.tableCell}>{trialInfo.sessionType}</Text>
                        </View>
                    </View>
                    <View style={styles.tableRow}>
                        <View style={styles.tableCol}>
                            <Text style={styles.tableCellHeader}>Note</Text>
                        </View>
                        <View style={styles.tableCol}>
                            <Text style={styles.tableCell}>{trialInfo.notes == "" ? trialInfo.clinician_notes : trialInfo.notes}</Text>
                        </View>
                    </View>
                </View>
                <Text style={styles.header}>Summary Metric</Text>
                {/* Summary table */}
                <View style={styles.table}>
                    {/* Table Header */}
                    <View style={styles.tableRow}>
                        <View style={styles.tableColHeader}>
                            <Text style={styles.tableCellHeader}>Metric</Text>
                        </View>
                        <View style={styles.tableColHeader}>
                            <Text style={styles.tableCellHeader}>Value</Text>
                        </View>
                    </View>
                    {/* Table Rows */}
                    {summaryData && Object.entries(tableContent).map(([key, value], index) => (
                        <View key={index} style={styles.tableRow}>
                            <View style={styles.tableCol}>
                                <Text style={styles.tableCellHeader}>{key}</Text>
                            </View>
                            <View style={styles.tableCol}>
                                <Text style={styles.tableCell}>{value}</Text>
                            </View>
                        </View>
                    ))}
                </View>
            </View>
        </Page>
        <Page size="A4" style={styles.page}>
            <View style={styles.section}>
                <Text style={styles.header}>Graphs</Text>
                {pdfCharts.map((item, index) => (
                    <View key={index} style={styles.imageContainer}>
                        <Text style={styles.imageTitle}>{item.title}</Text>
                        <Image src={item.src} style={styles.chartImage} />
                    </View>
                ))}
            </View>
        </Page>
    </Document>
);


export default PDFDocument;